import React, { useState, useEffect } from "react";
import { Upload, Cpu } from "lucide-react";
import { getJWTFromLocalStorage } from "../services/helpers";

const SERVER_URL = `http${process.env.REACT_APP_API_URL}`;

const ContentDetector = () => {
  const [statusText, setStatusText]   = useState("AWAITING INPUT");
  const [statusType, setStatusType]   = useState("idle"); // idle | scanning | safe | threat
  const [textInput, setTextInput]     = useState("");
  const [file, setFile]               = useState(null);
  const [user, setUser]               = useState(null);

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (!u) { window.location.href = "/login"; return; }
    setUser(JSON.parse(u));
  }, []);

  const classifyText = async () => {
    const token = await getJWTFromLocalStorage();
    if (!token) { window.location.href = "/login"; return; }
    setStatusText("SCANNING TEXT CONTENT…"); setStatusType("scanning");
    try {
      const resp = await fetch(`${SERVER_URL}/classify-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: textInput, recipient_id: user.id, source_platform: "KidsShield Guardian Portal" }),
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      const isHarmful = data.classification?.toLowerCase().includes("harmful");
      setStatusText(`CLASSIFICATION: ${data.classification?.toUpperCase() || "UNKNOWN"}`);
      setStatusType(isHarmful ? "threat" : "safe");
    } catch {
      setStatusText("SCAN FAILED — CHECK CONNECTION"); setStatusType("idle");
    }
  };

  const classifyImage = async () => {
    const token = await getJWTFromLocalStorage();
    if (!token) { window.location.href = "/login"; return; }
    setStatusText("ANALYZING IMAGE DATA…"); setStatusType("scanning");
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("recipient_id", user.id);
      form.append("source_platform", "KidsShield Guardian Portal");
      const resp = await fetch(`${SERVER_URL}/classify-image`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      const isHarmful = data.classification?.toLowerCase().includes("harmful");
      setStatusText(`CLASSIFICATION: ${data.classification?.toUpperCase() || "UNKNOWN"}`);
      setStatusType(isHarmful ? "threat" : "safe");
    } catch {
      setStatusText("SCAN FAILED — CHECK CONNECTION"); setStatusType("idle");
    }
  };

  const handleSubmit = async () => {
    if (file) await classifyImage();
    else if (textInput.trim()) await classifyText();
    else { setStatusText("NO INPUT DETECTED"); setStatusType("idle"); }
  };

  const statusConfig = {
    idle:     { color: "var(--text-muted)",    icon: "fa-circle",           bg: "rgba(255,255,255,0.03)" },
    scanning: { color: "var(--cyan-400)",       icon: "fa-spinner",          bg: "rgba(6,182,212,0.05)"  },
    safe:     { color: "var(--threat-low)",     icon: "fa-circle-check",     bg: "rgba(16,185,129,0.06)" },
    threat:   { color: "var(--threat-critical)",icon: "fa-triangle-exclamation", bg: "rgba(239,68,68,0.06)" },
  };
  const sc = statusConfig[statusType];

  return (
    <div className="content-detection">
      {/* Section label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
        <span style={{ fontSize: '0.5625rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          // CONTENT ANALYSIS TERMINAL
        </span>
        <div style={{ flex: 1, height: '1px', background: 'var(--border-dim)' }} />
      </div>

      <div className="detection-grid">
        {/* Input Panel */}
        <div className="panel input-panel">
          <h3 className="panel-title">CONTENT SCANNER</h3>
          <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '0.875rem', lineHeight: '1.5' }}>
            SUBMIT TEXT OR IMAGE FOR REAL-TIME THREAT ANALYSIS · RESULT NOT RECORDED
          </p>

          <div className="input-container">
            <textarea
              className="text-input"
              placeholder="// PASTE CONTENT FOR ANALYSIS…"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
            />
            <div className="upload-container">
              <input type="file" accept="image/*" onChange={e => setFile(e.target.files[0])} style={{ display: "none" }} id="img-upload" />
              <label htmlFor="img-upload" className="upload-btn" title="Upload Image">
                <Upload size={16} color="var(--purple-300)" />
              </label>
              {file && (
                <>
                  <div className="file-name" style={{ fontSize: '0.5625rem', maxWidth: '2.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                  <button onClick={() => setFile(null)} style={{ background: 'none', border: 'none', color: 'var(--threat-critical)', cursor: 'pointer', fontSize: '0.5625rem', fontFamily: 'var(--font-mono)' }}>CLEAR</button>
                </>
              )}
            </div>
          </div>

          <div className="submit-container">
            <button className="btn btn-primary" onClick={handleSubmit} style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', fontSize: '0.8125rem' }}>
              <Cpu size={14} />
              ANALYZE
            </button>
          </div>
        </div>

        {/* Status Panel */}
        <div className="panel status-panel" style={{ background: sc.bg, transition: 'background 0.4s ease' }}>
          <h3 className="panel-title">SCAN RESULT</h3>
          <div className="status-container">
            <div style={{
              width: '3.5rem', height: '3.5rem', borderRadius: '50%',
              background: `${sc.color}18`,
              border: `1px solid ${sc.color}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: statusType !== 'idle' ? `0 0 20px ${sc.color}44` : 'none',
              transition: 'all 0.4s ease',
            }}>
              <i className={`fa-solid ${sc.icon}${statusType === 'scanning' ? ' fa-spin' : ''}`}
                style={{ fontSize: '1.25rem', color: sc.color, filter: statusType !== 'idle' ? `drop-shadow(0 0 6px ${sc.color})` : 'none' }}
              />
            </div>

            <div style={{ textAlign: 'center' }}>
              <div className="status-text" style={{ color: sc.color, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.06em' }}>
                {statusText}
              </div>
              {statusType === 'scanning' && (
                <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} style={{ width: '3px', height: `${8 + (i % 3) * 4}px`, background: 'var(--cyan-400)', borderRadius: '2px', opacity: 0.7, animation: `pulse-live ${0.8 + i * 0.1}s ease-in-out infinite` }} />
                  ))}
                </div>
              )}
            </div>

            {(statusType === 'safe' || statusType === 'threat') && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginTop: '0.25rem' }}
                onClick={() => { setStatusText('AWAITING INPUT'); setStatusType('idle'); setTextInput(''); setFile(null); }}
              >
                CLEAR SCAN
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentDetector;
